import { useEffect, useRef } from "react";
import { create, type StoreApi } from "zustand";
import { wsClient } from "./ws-client";
import { useConnectionStore } from "../stores/connection";

const DEBOUNCE_MS = 300;

interface LockInfo {
  userId: number;
  userName: string;
  instanceId: string;
}

export interface FieldHandle<T = string> {
  value: T;
  onChange: (val: T) => void;
  onFocus: () => void;
  onBlur: () => void;
  lockedBy: LockInfo | null;
  isEditable: boolean;
  isDirty: boolean;
}

interface FormState {
  serverData: Record<string, unknown>;
  dirtyFields: Set<string>;
  locks: Record<string, LockInfo>;
  instanceId: string;
  isSubmitting: boolean;
  isDirty: boolean;
  isConnected: boolean;
  field: <T = string>(fieldName: string) => FieldHandle<T>;
  submit: () => Promise<unknown>;
  discard: () => Promise<void>;
}

interface InstanceEntry {
  store: StoreApi<FormState>;
  useStore: StoreApi<FormState> & ((selector: (s: FormState) => any) => any);
  refCount: number;
  cleanup: () => void;
}

export function createLiveFormStore(config: { entity: string }) {
  const instances = new Map<number, InstanceEntry>();

  return function useLiveFormInstance(entityId: number | undefined) {
    const entryRef = useRef<InstanceEntry | null>(null);
    const entityIdRef = useRef(entityId);
    entityIdRef.current = entityId;

    useEffect(() => {
      if (entityId === undefined) return;

      let entry = instances.get(entityId);
      if (!entry) {
        entry = createFormInstance(config.entity, entityId);
        instances.set(entityId, entry);
      }
      entry.refCount++;
      entryRef.current = entry;

      return () => {
        entry!.refCount--;
        if (entry!.refCount === 0) {
          entry!.cleanup();
          instances.delete(entityId);
          entryRef.current = null;
        }
      };
    }, [entityId]);

    if (entityId === undefined || !instances.has(entityId)) {
      return undefined;
    }
    return instances.get(entityId)!.useStore as any;
  };
}

function createFormInstance(entity: string, entityId: number): InstanceEntry {
  const debounceTimers: Record<string, ReturnType<typeof setTimeout>> = {};
  const instanceId = wsClient.instanceId;

  const store = create<FormState>()((set, get) => ({
    serverData: {},
    dirtyFields: new Set<string>(),
    locks: {},
    instanceId,
    isSubmitting: false,
    isDirty: false,
    isConnected: useConnectionStore.getState().status === "connected",

    field: <T = string>(fieldName: string): FieldHandle<T> => {
      const state = get();
      const value = state.serverData[fieldName] as T;
      const lock = state.locks[fieldName] || null;
      const isLockedByOther = lock !== null && lock.instanceId !== instanceId;

      return {
        value,
        lockedBy: isLockedByOther ? lock : null,
        isEditable: !isLockedByOther,
        isDirty: state.dirtyFields.has(fieldName),
        onChange: (val: T) => {
          set((prev) => {
            const next = new Set(prev.dirtyFields);
            next.add(fieldName);
            return {
              serverData: { ...prev.serverData, [fieldName]: val },
              dirtyFields: next,
              isDirty: true,
            };
          });
          if (debounceTimers[fieldName]) clearTimeout(debounceTimers[fieldName]);
          debounceTimers[fieldName] = setTimeout(() => {
            if (useConnectionStore.getState().status === "connected") {
              wsClient.rpc("field.edit", { entity, entityId, field: fieldName, value: val }).catch(() => {});
            }
          }, DEBOUNCE_MS);
        },
        onFocus: () => {
          if (useConnectionStore.getState().status === "connected") {
            wsClient.rpc("lock.acquire", { entity, entityId, field: fieldName }).catch(() => {});
          }
        },
        onBlur: () => {
          if (useConnectionStore.getState().status === "connected") {
            wsClient.rpc("lock.release", { entity, entityId, field: fieldName }).catch(() => {});
          }
        },
      };
    },

    submit: async () => {
      const state = get();
      if (!state.isDirty || state.isSubmitting) return;
      set({ isSubmitting: true });
      try {
        const result = await wsClient.rpc("entity.save", { entity, entityId });
        set({ dirtyFields: new Set(), isDirty: false });
        return result;
      } finally {
        set({ isSubmitting: false });
      }
    },

    discard: async () => {
      try {
        await wsClient.rpc("entity.discard", { entity, entityId });
      } catch {}
      set({ dirtyFields: new Set(), isDirty: false });
    },
  }));

  const topic = entity === "asset" ? `assets.item.${entityId}` : `${entity}.item.${entityId}`;

  const dataSub = wsClient.subscribe(topic, (data) => {
    if (data && typeof data === "object") {
      if ("action" in (data as Record<string, unknown>)) {
        const event = data as { action: string; data: unknown };
        if (event.data && typeof event.data === "object") {
          store.setState({ serverData: event.data as Record<string, unknown> });
        }
      } else {
        store.setState({ serverData: data as Record<string, unknown> });
      }
    }
  });

  const lockSub = wsClient.subscribe(`locks.${entity}.${entityId}`, (data) => {
    if (data && typeof data === "object") {
      store.setState({ locks: data as Record<string, LockInfo> });
    }
  });

  const editSub = wsClient.subscribe(`edits.${entity}.${entityId}`, (data) => {
    if (data && typeof data === "object") {
      const edit = data as { field: string; value: unknown; instanceId: string };
      if (edit.instanceId !== instanceId) {
        const state = store.getState();
        store.setState({
          serverData: { ...state.serverData, [edit.field]: edit.value },
        });
      }
    }
  });

  wsClient
    .rpc<Record<string, unknown>>("field.getEdits", { entity, entityId })
    .then((edits) => {
      if (edits && Object.keys(edits).length > 0) {
        const state = store.getState();
        const next = new Set(state.dirtyFields);
        for (const key of Object.keys(edits)) next.add(key);
        store.setState({
          serverData: { ...state.serverData, ...edits },
          dirtyFields: next,
          isDirty: next.size > 0,
        });
      }
    })
    .catch(() => {});

  const connUnsub = useConnectionStore.subscribe((conn) => {
    store.setState({ isConnected: conn.status === "connected" });
  });

  const cleanup = () => {
    for (const timer of Object.values(debounceTimers)) clearTimeout(timer);
    wsClient.unsubscribe(dataSub);
    wsClient.unsubscribe(lockSub);
    wsClient.unsubscribe(editSub);
    connUnsub();
  };

  return { store, useStore: store as any, refCount: 0, cleanup };
}

export function createQaFormStore() {
  return function useQaForm(
    queueId: number | undefined,
    initialAnswers: Record<string, string>
  ) {
    const debounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
    const storeRef = useRef<StoreApi<QaFormState> | null>(null);
    const prevQueueId = useRef<number | undefined>(undefined);
    const connected = useConnectionStore((s) => s.status === "connected");

    if (!storeRef.current) {
      storeRef.current = create<QaFormState>()(() => ({
        editValues: {},
        dirtyFields: new Set<string>(),
        locks: {},
        isDirty: false,
        answers: { ...initialAnswers },
      }));
    }

    const store = storeRef.current;

    useEffect(() => {
      if (queueId !== prevQueueId.current) {
        store.setState({
          editValues: {},
          dirtyFields: new Set(),
          locks: {},
          isDirty: false,
          answers: { ...initialAnswers },
        });
        prevQueueId.current = queueId;
      }
    }, [queueId, initialAnswers]);

    useEffect(() => {
      if (!connected || queueId === undefined) return;

      const lockSub = wsClient.subscribe(`locks.qa.${queueId}`, (data) => {
        if (data && typeof data === "object") {
          store.setState({ locks: data as Record<string, LockInfo> });
        }
      });

      const editSub = wsClient.subscribe(`edits.qa.${queueId}`, (data) => {
        if (data && typeof data === "object") {
          const edit = data as { field: string; value: unknown; instanceId: string };
          if (edit.instanceId !== wsClient.instanceId) {
            const state = store.getState();
            const newEdits = { ...state.editValues, [edit.field]: String(edit.value) };
            store.setState({
              editValues: newEdits,
              answers: { ...initialAnswers, ...newEdits },
            });
          }
        }
      });

      wsClient
        .rpc<Record<string, unknown>>("field.getEdits", { entity: "qa", entityId: queueId })
        .then((edits) => {
          if (edits && Object.keys(edits).length > 0) {
            const strEdits: Record<string, string> = {};
            for (const [k, v] of Object.entries(edits)) strEdits[k] = String(v);
            const state = store.getState();
            const next = new Set(state.dirtyFields);
            for (const key of Object.keys(edits)) next.add(key);
            const newEdits = { ...state.editValues, ...strEdits };
            store.setState({
              editValues: newEdits,
              dirtyFields: next,
              isDirty: next.size > 0,
              answers: { ...initialAnswers, ...newEdits },
            });
          }
        })
        .catch(() => {});

      return () => {
        wsClient.unsubscribe(lockSub);
        wsClient.unsubscribe(editSub);
      };
    }, [connected, queueId, initialAnswers]);

    useEffect(() => {
      return () => {
        for (const timer of Object.values(debounceTimers.current)) clearTimeout(timer);
      };
    }, []);

    const field = (qaId: number): QaFieldHandle => {
      const key = String(qaId);
      const state = store.getState();
      const value = state.editValues[key] ?? initialAnswers[key] ?? "";
      const lock = state.locks[key] || null;
      const isLockedByOther = lock !== null && lock.instanceId !== wsClient.instanceId;

      return {
        value,
        lockedBy: isLockedByOther ? lock : null,
        isEditable: !isLockedByOther,
        isDirty: state.dirtyFields.has(key),
        onChange: (val: string) => {
          const s = store.getState();
          const next = new Set(s.dirtyFields);
          next.add(key);
          const newEdits = { ...s.editValues, [key]: val };
          store.setState({
            editValues: newEdits,
            dirtyFields: next,
            isDirty: true,
            answers: { ...initialAnswers, ...newEdits },
          });

          if (debounceTimers.current[key]) clearTimeout(debounceTimers.current[key]);
          debounceTimers.current[key] = setTimeout(() => {
            if (useConnectionStore.getState().status === "connected") {
              wsClient.rpc("field.edit", { entity: "qa", entityId: queueId, field: key, value: val }).catch(() => {});
            }
          }, DEBOUNCE_MS);
        },
        onFocus: () => {
          if (useConnectionStore.getState().status === "connected") {
            wsClient.rpc("lock.acquire", { entity: "qa", entityId: queueId, field: key }).catch(() => {});
          }
        },
        onBlur: () => {
          if (useConnectionStore.getState().status === "connected") {
            wsClient.rpc("lock.release", { entity: "qa", entityId: queueId, field: key }).catch(() => {});
          }
        },
      };
    };

    const submit = async () => {
      const state = store.getState();
      if (!state.isDirty || queueId === undefined) return;
      const result = await wsClient.rpc("entity.save", { entity: "qa", entityId: queueId });
      store.setState({ dirtyFields: new Set(), isDirty: false, editValues: {} });
      return result;
    };

    const discard = async () => {
      if (queueId === undefined) return;
      try {
        await wsClient.rpc("entity.discard", { entity: "qa", entityId: queueId });
      } catch {}
      store.setState({ dirtyFields: new Set(), isDirty: false, editValues: {} });
    };

    const state = store.getState();
    return { field, answers: state.answers, isDirty: state.isDirty, submit, discard };
  };
}

interface QaFormState {
  editValues: Record<string, string>;
  dirtyFields: Set<string>;
  locks: Record<string, LockInfo>;
  isDirty: boolean;
  answers: Record<string, string>;
}

export interface QaFieldHandle {
  value: string;
  onChange: (val: string) => void;
  onFocus: () => void;
  onBlur: () => void;
  lockedBy: LockInfo | null;
  isEditable: boolean;
  isDirty: boolean;
}
